'use client';

import { useCallback, useRef } from 'react';

export function useSafePrefetch<T>(
  prefetchFn: (item: T) => void,
  debounceMs = 150
) {
  const prefetchedItemsRef = useRef<Set<string>>(new Set());
  const prefetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const safePrefetch = useCallback(
    (item: T, key?: string) => {
      // Clear any existing timeout
      if (prefetchTimeoutRef.current) {
        clearTimeout(prefetchTimeoutRef.current);
      }

      // Create a unique key for the item
      const itemKey = key ?? String(item);

      // Check if already prefetched
      if (prefetchedItemsRef.current.has(itemKey)) {
        return;
      }

      // Debounce the prefetch to avoid rapid calls
      prefetchTimeoutRef.current = setTimeout(() => {
        // Double-check we haven't prefetched this item yet
        if (!prefetchedItemsRef.current.has(itemKey)) {
          prefetchedItemsRef.current.add(itemKey);
          try {
            prefetchFn(item);
          } catch {
            // Remove from set if prefetch fails so it can be retried
            prefetchedItemsRef.current.delete(itemKey);
          }
        }
      }, debounceMs);
    },
    [prefetchFn, debounceMs]
  );

  const clearPrefetchHistory = useCallback(() => {
    prefetchedItemsRef.current.clear();
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current);
      prefetchTimeoutRef.current = null;
    }
  }, []);

  return {
    safePrefetch,
    clearPrefetchHistory,
  };
}
